import { z } from 'zod';
import { objectIdSchema } from './common.schema';
import { officerSummarySchema } from './officer.schema';
import { orgUnitSummarySchema } from './orgUnit.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C6a] A COMMAND ROLE ASKED OF A DIVISION — THE CONTRACT.
 *
 *  A command role on a duty point is filled BY NAME (`commandEntries[].officerIds`, unchanged and
 *  still the default) or, since C6a, ASKED of a division and optionally a unit. The division then
 *  names the officers, or hands a position on to one of its own units, from the fill screen.
 *
 *  ⚠️ ONE ROW PER POSITION, NEVER A QUANTITY. "Ask Central for two Assisting Inspectors" is TWO
 *  asks and, once issued, TWO seats. There is no count field anywhere in this file, in the model
 *  or in the service, and that is the whole of §3's decision: FR-5.2.6b puts command outside every
 *  force number, and R1 (docs/status-matrix.md) is what an exclusion written as a filter over a
 *  shared number turns into six passes later — vans sit in `allocated_count`, every `$sum` reader
 *  sees them, and a post with a van can never read NAMED. Here there is no number to filter out,
 *  so nothing can stop filtering. `commandRoleIsolation.test.ts` CLAIM 6 bans aggregation words in
 *  this file by TOKEN, so a `seatCount` added here later goes red.
 *
 *  ⚠️ "AWAITED" IS AN EMPTINESS QUESTION — a seat whose officer is `null` — and it is answered in
 *  ONE predicate (`seatIsAwaited`, `utils/commandSeat.util.ts`) plus its one MQL twin in the API.
 *
 *  ⚠️ NOTHING HERE ENTERS A FORCE TOTAL. No field on this file is read by `planSlots`, by
 *  `allocation_fills`, by `SHARE_ACCUMULATORS` or by any dashboard service — the seats are a
 *  collection of their own (`command_seats`), and the requirement lines never learn they exist.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * A minted position id — `crypto.getRandomValues` on the client, `randomBytes` on the server when a
 * body omits one. The duty point id's shape exactly, because it is the same rule for the same
 * reason: an identity carried on the wire outlives a wholesale array replace, and one derived from
 * a position re-points the moment a row is inserted above it (CLAUDE.md §7).
 */
export const commandAskIdSchema = z.string().trim().min(1).max(24);

/**
 * ⚠️ ONE ASKED POSITION ON A COMMAND ROW, AS THE BUILDER SENDS IT.
 *
 * The division is mandatory and the unit is optional — FR-5.2.7's shape for a force source, one
 * vocabulary over. Validated for TYPE and COHERENCE on the write path exactly as a force
 * allocation is (`assertAllocationOrgUnits`, generalised in C6a).
 */
export const commandAskInputSchema = z.object({
  askId: commandAskIdSchema.nullish(),
  divisionOrgUnitId: objectIdSchema,
  /** Blank means "any unit under this division" — the division names, or hands on. */
  unitOrgUnitId: objectIdSchema.nullish(),
});

/**
 * ⚠️ WHO HOLDS AN ASKED POSITION ONCE THE ORDER IS ISSUED. `null` on a draft, which has no seats.
 *
 * `addressee` is the CURRENT addressee — the unit a division handed the position to, or whatever
 * the order asked. `handedDown` says which. `officer` is who the addressee named, read-only here:
 * it is NEVER written back into `officerIds`, because the creator's next autosave would then send
 * a division's choice back as a by-name officer — [C1b §3]'s preserved-key hazard, one field over.
 */
export const commandAskSeatSchema = z.object({
  addressee: orgUnitSummarySchema,
  handedDown: z.boolean(),
  officer: officerSummarySchema.nullable(),
});

/** An asked position on the order DTO. */
export const commandAskSchema = z.object({
  askId: z.string(),
  division: orgUnitSummarySchema,
  unit: orgUnitSummarySchema.nullable(),
  seat: commandAskSeatSchema.nullable(),
});

// ── The fill screen ──────────────────────────────────────────────────────────────

/**
 * ⚠️ ONE SEAT ON THE FILL SCREEN. It is deliberately NOT a `FillSlotDto`: it has no `slotRef`, no
 * allocation and no line, so nothing that walks force slots (`slotSequence`, `countNamed`,
 * `sheetDischarged`'s force half) can receive one. `web/src/lib/commandIsolation.test.ts` (ii) pins
 * it with a `@ts-expect-error` apiece: a seat is not a `FillSlotDto`, and its family is not a
 * `FillLineDto` to any of the three.
 */
export const fillCommandSeatSchema = z.object({
  seatId: objectIdSchema,
  askId: z.string(),
  /** The CURRENT addressee: the handed-down unit, the asked unit, or the division itself. */
  addressee: orgUnitSummarySchema,
  handedDown: z.boolean(),
  officer: officerSummarySchema.nullable(),
});

/**
 * ⚠️ ONE ROLE, ONE ASKED DIVISION — "a family". The rows of one role asked of two divisions are two
 * families, because each division answers for its own and hands on inside itself.
 *
 * `mayHandDown` is true only for positions the order asked of the DIVISION with no unit — a unit
 * asked directly has nothing beneath it to hand to, and a position already handed down never
 * moves again from the unit's side (the one-level rule force delegation keeps).
 *
 * `rankIds` is the role's eligible set, EXPANDED ([C2b §5]) — the officer search filters by exactly
 * this, and the server refuses by exactly this. `[]` means any rank (a one-off row).
 */
export const fillCommandFamilySchema = z.object({
  commandKey: z.string(),
  label: z.string(),
  rankIds: z.array(objectIdSchema),
  division: orgUnitSummarySchema,
  mayHandDown: z.boolean(),
  seats: z.array(fillCommandSeatSchema).min(1),
});

/**
 * ⚠️ [C6a S1b] WHAT EVERY WRITE ON A SEAT ANSWERS WITH — the caller's families on that duty, read
 * again after the commit, narrowed by the same optional `?division=` the fill sheet takes. A write
 * returns the rows the screen redraws from, never an empty 204 ([FIX-204]: this client cannot read
 * one), and never the whole fill sheet: the force half of that sheet is unchanged by any of these
 * verbs, so re-reading it would be a burst of force reads for a command answer.
 */
export const commandFamiliesResultSchema = z.object({
  families: z.array(fillCommandFamilySchema),
});

/** Name an officer into one EMPTY seat. The seat is addressed by its id, never by a position. */
export const nameCommandSeatSchema = z.object({
  officerId: objectIdSchema,
});

/**
 * ⚠️ HAND EMPTY POSITIONS TO A UNIT, OR BACK TO THE DIVISION — BY SEAT ID.
 *
 * Each target re-addresses ONE seat: `unitOrgUnitId` is the unit it goes to, `null` takes it back to
 * the division. Only EMPTY seats of a division-asked family move, only to a unit inside that
 * division ([C1b] §3e — refused on the server, the picker is not a guard), and never to a unit the
 * order already asks directly for the same role. A seat not listed is left exactly where it is.
 */
export const handDownCommandSeatsSchema = z.object({
  commandKey: z.string().trim().min(1).max(120),
  divisionOrgUnitId: objectIdSchema,
  targets: z
    .array(z.object({ seatId: objectIdSchema, unitOrgUnitId: objectIdSchema.nullable() }))
    .min(1)
    .max(60),
});

/** Which units a division may hand a position to — its active descendants, minus the units asked directly. */
export const commandHandDownOptionsQuerySchema = z.object({
  commandKey: z.string().trim().min(1).max(120),
  divisionOrgUnitId: objectIdSchema,
});

export const commandHandDownOptionsSchema = z.object({
  units: z.array(orgUnitSummarySchema),
});

// ── What is still awaited, as WORDS ─────────────────────────────────────────────

/**
 * ⚠️ AN AWAITED ROLE, AS LABELS — never a number. `from` holds DISTINCT addressee codes (`CD`,
 * `CD · TALA`), one per addressee still owing a position, never one per empty seat: two empty
 * seats asked of Central read "awaited from CD" once, which is a statement rather than a count
 * spelled as identities.
 */
export const awaitedCommandSchema = z.object({
  label: z.string(),
  from: z.array(z.string()).min(1),
});
